import { ReactNode, useState } from 'react';
import ModernSidebar from '../layout/ModernSidebar';
import EnhancedModernTopbar, { TopbarBackgroundStyle } from '../layout/EnhancedModernTopbar';
import Breadcrumb, { BreadcrumbItem } from '../shared/Breadcrumb';
import NetworkStatusBanner from '../shared/NetworkStatusBanner';

interface SearchField {
  id: string;
  label: string;
  placeholder: string;
}

interface MainContainerProps {
  children: ReactNode;
  title?: string;
  showTopbar?: boolean;
  // Background styling
  topbarBackgroundStyle?: TopbarBackgroundStyle;
  // Breadcrumb functionality
  breadcrumbItems?: BreadcrumbItem[];
  showBackButton?: boolean;
  onBack?: () => void;
  // Search functionality
  enableSearch?: boolean;
  searchFields?: SearchField[];
  onSearch?: (query: string, field: string) => void;
  onSearchFieldChange?: (field: string) => void;
  initialSearchQuery?: string;
  initialSearchField?: string;
  enableScan?: boolean;
  // Enable user logout section in the topbar
  enableUserLogoutSection?: boolean;
  // Action buttons
  primaryAction?: {
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary';
  };
  customHeader?: ReactNode;
  showSidebar?: boolean;
  enableWideScreen?: boolean;
  // Opt-in "app-shell" layout: on lg+ the page itself does NOT scroll — the content fills the
  // viewport and an inner region (e.g. a data-table body) owns the scroll, so filters/headers/
  // pager stay pinned. Mobile keeps normal full-page scroll. Default false (every other page
  // is unchanged).
  fitToViewport?: boolean;
}

const MainContainer: React.FC<MainContainerProps> = ({ 
  children, 
  title,
  showTopbar = true,
  showSidebar = true,
  showBackButton = false,
  topbarBackgroundStyle = TopbarBackgroundStyle.DEFAULT,
  breadcrumbItems = [],
  onBack,
  enableSearch = false,
  searchFields = [],
  onSearch,
  onSearchFieldChange,
  initialSearchQuery,
  initialSearchField,
  enableScan = false,
  enableUserLogoutSection = true,
  primaryAction,
  customHeader,
  enableWideScreen = true,
  fitToViewport = false,
}) => {
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);

  const toggleSidebarCollapse = () => setIsSidebarCollapsed(!isSidebarCollapsed);
  const wScreenStyle = enableWideScreen ? 'w-screen' : '';
  return (
    <div className={`flex h-dvh ${wScreenStyle} overflow-hidden bg-gray-50`}>
      {/* Mobile Sidebar Overlay */}
      {showSidebar && isMobileSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      {showSidebar && <div className="hidden lg:block h-full">
        <ModernSidebar 
          isOpen={true} 
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={toggleSidebarCollapse}
        />
      </div>}

      {/* Mobile Sidebar */}
      {showSidebar && <div className="lg:hidden">
        <ModernSidebar
          isOpen={isMobileSidebarOpen}
          isCollapsed={false}
          onToggleCollapse={() => setIsMobileSidebarOpen(false)}
        />
      </div>}

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Topbar */}
        {showTopbar && (
          <EnhancedModernTopbar 
            title={title}
            backgroundStyle={topbarBackgroundStyle}
            showMenuButton={showSidebar}
            onMenuClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
            showCollapseButton={showSidebar}
            onCollapseClick={toggleSidebarCollapse}
            isSidebarCollapsed={isSidebarCollapsed}
            enableSearch={enableSearch}
            searchFields={searchFields}
            onSearch={onSearch}
            onSearchFieldChange={onSearchFieldChange}
            initialSearchQuery={initialSearchQuery}
            initialSearchField={initialSearchField}
            enableScan={enableScan}
            primaryAction={primaryAction}
            children={customHeader}
            enableUserLogoutSection={enableUserLogoutSection}
          />
        )}
        
        {/* Breadcrumb Section */}
        {breadcrumbItems.length > 0 && (
          <div className="border-b border-gray-200 bg-white px-4 py-3">
            <Breadcrumb 
              items={breadcrumbItems}
              showBackButton={showBackButton}
              onBack={onBack}
            />
          </div>
        )}
        
        {/* Main content with scroll. In fitToViewport mode the page stops scrolling on lg+
            (overflow-hidden) and the child fills the viewport height (h-full) so an inner
            region can own the scroll; mobile always keeps normal full-page scroll. */}
        <NetworkStatusBanner />
        <main
          className={`flex-1 overflow-y-auto bg-gray-50 pb-16 lg:pb-0 ${
            fitToViewport ? 'lg:overflow-hidden' : ''
          }`}
        >
          <div className={`min-h-full ${fitToViewport ? 'lg:h-full lg:min-h-0' : ''}`}>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default MainContainer;
